import React from 'react';
import { useTranslation } from 'react-i18next';
import { MapPin, Phone, Mail, Users, Edit, X } from 'lucide-react';

import { Company } from "../../model/professional/compagny-model";

// Types
interface CompanyViewProps {
  isOpen: boolean;
  company?: Company;
  onClose: () => void;
  onEdit: () => void;
  onViewCollaborators: () => void;
}

const CompanyDetailView: React.FC<CompanyViewProps> = ({
  isOpen,
  company,
  onClose,
  onEdit,
  onViewCollaborators
}) => {
  const { t } = useTranslation();

  // Ne rien afficher si le modal n'est pas ouvert ou si l'entreprise n'est pas définie
  if (!isOpen || !company) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4 flex justify-between items-center z-10">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            {t('company.view.title', 'Visualisation de l\'entreprise')}
          </h2>
          <div className="flex items-center gap-2">
            <button
              onClick={onEdit}
              className="flex items-center gap-2 px-3 py-2 bg-primary text-white rounded-md hover:bg-primary-dark transition-colors"
            >
              <Edit size={16} />
              <span>{t('company.view.edit', 'Modifier')}</span>
            </button>
            <button
              onClick={onClose}
              className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            >
              <X size={20} className="text-gray-500 dark:text-gray-400" />
            </button>
          </div>
        </div>

        <div className="p-6 space-y-6">
        {/* En-tête avec logo et nom */}
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center space-x-4">
            <div className="flex-shrink-0">
              <img 
                src={company.companyLogo || '/avatar-placeholder.jpg'} 
                alt={company.businessName} 
                className="h-20 w-20 rounded-full object-cover border-2 border-gray-200 dark:border-gray-700" 
              />
            </div>
            <div>
              <h3 className="text-xl font-bold text-gray-900 dark:text-white">{company.businessName}</h3>
              <p className="text-gray-500 dark:text-gray-400">
                {t('company.view.id', 'ID')}: {company.id}
              </p>
            </div>
          </div>
        </div>
        
        {/* Informations de l'entreprise */}
        <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <h4 className="text-lg font-medium text-gray-900 dark:text-white">
              {t('company.view.contactInfo', 'Informations de contact')}
            </h4>
            
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <MapPin className="text-gray-400 mt-0.5 flex-shrink-0" size={18} />
                <div>
                  <p className="text-gray-700 dark:text-gray-300">{company.address}</p>
                  <p className="text-gray-700 dark:text-gray-300">{company.postalCode} {company.city}</p>
                </div>
              </div>

              {company?.phone && (
                <li className="flex items-center">
                  <Phone className="h-5 w-5 text-gray-400 mr-2" />
                  <span className="text-gray-700 dark:text-gray-300">{company.phone}</span>
                </li>
              )}

              <li className="flex items-center">
                <Mail className="h-5 w-5 text-gray-400 mr-2" />
                <span className="text-gray-700 dark:text-gray-300">{company?.email}</span>
              </li>
            </div>
          </div>
          
          <div className="space-y-4">
            <h4 className="text-lg font-medium text-gray-900 dark:text-white">
              {t('company.view.details', 'Détails')}
            </h4>
            
            {company.description && (
              <div className="space-y-2">
                <h5 className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  {t('company.view.description', 'Description')}
                </h5>
                <p className="text-gray-600 dark:text-gray-400 whitespace-pre-wrap">
                  {company.description}
                </p>
              </div>
            )}
            
            <div className="space-y-2">
              <h5 className="text-sm font-medium text-gray-700 dark:text-gray-300">
                {t('company.view.collaborators', 'Collaborateurs')}
              </h5>
              <button 
                onClick={onViewCollaborators}
                className="flex items-center gap-2 text-primary hover:underline"
              >
                <Users size={18} />
                <span>
                  {parseInt(String(company.collaboratorsCount || '0'))}
                  {parseInt(String(company.collaboratorsCount || '0')) > 1 
                    ? t('company.view.collaboratorsPlural', 'collaborateurs') 
                    : t('company.view.collaboratorsSingular', 'collaborateur')}
                </span>
              </button>
            </div>
          </div>
        </div>

        {/* Galerie de photos */}
        {company.galleryPhoto && (() => {
          // Convertir galleryPhoto en tableau si c'est une string
          const photos = typeof company.galleryPhoto === 'string'
            ? company.galleryPhoto.split(',').filter(url => url.trim())
            : company.galleryPhoto;

          if (!photos || photos.length === 0) return null;

          return (
            <div className="p-6 border-t border-gray-200 dark:border-gray-700">
              <h4 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                {t('company.view.galleryPhotos', 'Galerie de photos')}
              </h4>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {photos.map((photoUrl: string, index: number) => (
                  <div
                    key={`gallery-${index}`}
                    className="relative aspect-square rounded-lg overflow-hidden border-2 border-gray-300 dark:border-gray-600 hover:border-primary dark:hover:border-primary-light transition-colors"
                  >
                    <img
                      src={photoUrl}
                      alt={`Photo ${index + 1}`}
                      className="w-full h-full object-cover cursor-pointer hover:scale-105 transition-transform"
                      onClick={() => window.open(photoUrl, '_blank')}
                    />
                    {index === 0 && (
                      <div className="absolute top-2 left-2 bg-primary/80 text-white text-xs px-2 py-1 rounded">
                        {t('company.view.mainPhoto', 'Photo principale')}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          );
        })()}
        </div>
      </div>
    </div>
  );
};

export default CompanyDetailView;
